<ng-helmet>
  <title>Contact Us | Uptal</title>
</ng-helmet>
<div>
  <div
    class="page-max-width !max-w-screen-md page-default-padding py-12 md:py-20 min-h-[820px]"
  >
    <p
      data-aos="fade"
      [attr.data-aos-delay]="200"
      class="text-center text-2xl md:text-3xl font-bold text-gray-700 pb-3"
    >
      {{ "CONTACT.TITLE" | translate }}
    </p>
    <p class="text-center text-sm md:text-base text-gray-500 pb-10">
      {{ "CONTACT.INTRO" | translate }}
    </p>

    <!-- Success: the form is replaced by the ticket receipt. -->
    <div
      *ngIf="status === 'success'; else contactForm"
      class="contact-card rounded-2xl border border-gray-200 bg-white p-8 md:p-10 text-center shadow-sm"
    >
      <div
        class="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-50"
      >
        <mat-icon class="!h-auto !w-auto !text-4xl text-green-600">
          check_circle
        </mat-icon>
      </div>
      <p class="text-xl md:text-2xl font-bold text-gray-700">
        {{ "CONTACT.SUCCESS.TITLE" | translate }}
      </p>
      <p class="mt-2 text-gray-600">
        {{ "CONTACT.SUCCESS.BODY" | translate }}
      </p>
      <div
        *ngIf="reference"
        class="mt-6 inline-flex flex-wrap items-center justify-center gap-x-2 rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-700"
      >
        <span>{{ "CONTACT.SUCCESS.REFERENCE" | translate }}</span>
        <!-- The reference is an ASCII code: keep it LTR in Arabic. -->
        <span class="font-bold tracking-wide" dir="ltr">{{ reference }}</span>
      </div>
      <button
        type="button"
        class="button primary mx-auto mt-8 justify-center"
        (click)="sendAnother()"
      >
        {{ "CONTACT.SUCCESS.SEND_ANOTHER" | translate }}
      </button>
    </div>

    <ng-template #contactForm>
      <form
        class="contact-form textfield relative"
        [formGroup]="form"
        (ngSubmit)="submit()"
        novalidate
      >
        <!--
          Honeypot. Offscreen, aria-hidden and untabbable: humans never see or
          reach it, bots fill it in. Posted as `website` for the backend to judge.
        -->
        <div class="contact-honeypot" aria-hidden="true">
          <label for="contact-website">Website</label>
          <input
            id="contact-website"
            type="text"
            formControlName="website"
            autocomplete="off"
            tabindex="-1"
          />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
          <div class="md:col-span-2">
            <label class="label" for="contact-reason">
              {{ "CONTACT.FIELDS.REASON" | translate }}
            </label>
            <select
              id="contact-reason"
              class="input contact-select"
              [class.contact-select-empty]="!form.get('reason')?.value"
              formControlName="reason"
            >
              <option value="" disabled>
                {{ "CONTACT.REASONS.PLACEHOLDER" | translate }}
              </option>
              <option *ngFor="let reason of reasons" [value]="reason.value">
                {{ reason.label | translate }}
              </option>
            </select>
            <p class="validation mt-1" *ngIf="fieldErrors['reason']">
              {{ fieldErrors["reason"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('reason', 'required')">
              {{ "CONTACT.VALIDATION.REASON_REQUIRED" | translate }}
            </p>
          </div>

          <div>
            <label class="label" for="contact-name">
              {{ "CONTACT.FIELDS.NAME" | translate }}
            </label>
            <input
              id="contact-name"
              class="input"
              type="text"
              formControlName="name"
              autocomplete="name"
            />
            <p class="validation mt-1" *ngIf="fieldErrors['name']">
              {{ fieldErrors["name"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('name', 'required')">
              {{ "CONTACT.VALIDATION.NAME_REQUIRED" | translate }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('name', 'maxlength')">
              {{ "CONTACT.VALIDATION.TOO_LONG" | translate }}
            </p>
          </div>

          <div>
            <label class="label" for="contact-email">
              {{ "CONTACT.FIELDS.EMAIL" | translate }}
            </label>
            <input
              id="contact-email"
              class="input"
              type="email"
              formControlName="email"
              autocomplete="email"
              inputmode="email"
            />
            <p class="validation mt-1" *ngIf="fieldErrors['email']">
              {{ fieldErrors["email"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('email', 'required')">
              {{ "CONTACT.VALIDATION.EMAIL_REQUIRED" | translate }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('email', 'email')">
              {{ "CONTACT.VALIDATION.EMAIL_INVALID" | translate }}
            </p>
          </div>

          <div>
            <label class="label" for="contact-phone">
              {{ "CONTACT.FIELDS.PHONE" | translate }}
            </label>
            <input
              id="contact-phone"
              class="input"
              type="tel"
              formControlName="phone"
              autocomplete="tel"
              dir="ltr"
            />
            <p class="validation mt-1" *ngIf="fieldErrors['phone']">
              {{ fieldErrors["phone"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('phone', 'required')">
              {{ "CONTACT.VALIDATION.PHONE_REQUIRED" | translate }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('phone', 'maxlength')">
              {{ "CONTACT.VALIDATION.TOO_LONG" | translate }}
            </p>
          </div>

          <div>
            <label class="label" for="contact-company">
              {{ "CONTACT.FIELDS.COMPANY" | translate }}
              <span class="text-gray-400">
                {{ "CONTACT.FIELDS.OPTIONAL" | translate }}
              </span>
            </label>
            <input
              id="contact-company"
              class="input"
              type="text"
              formControlName="company"
              autocomplete="organization"
            />
            <p class="validation mt-1" *ngIf="fieldErrors['company']">
              {{ fieldErrors["company"] }}
            </p>
          </div>

          <div class="md:col-span-2">
            <label class="label" for="contact-subject">
              {{ "CONTACT.FIELDS.SUBJECT" | translate }}
            </label>
            <input
              id="contact-subject"
              class="input"
              type="text"
              formControlName="subject"
            />
            <p class="validation mt-1" *ngIf="fieldErrors['subject']">
              {{ fieldErrors["subject"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('subject', 'required')">
              {{ "CONTACT.VALIDATION.SUBJECT_REQUIRED" | translate }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('subject', 'maxlength')">
              {{ "CONTACT.VALIDATION.TOO_LONG" | translate }}
            </p>
          </div>

          <!--
            Job seekers land here by mistake: point them at Auto-Apply without
            blocking the form — the message below still reaches the team.
          -->
          <div
            *ngIf="showJobSeekerNudge"
            class="contact-nudge md:col-span-2 rounded-xl border border-blue-200 bg-blue-50 p-4 md:p-5"
          >
            <div class="flex items-start gap-x-3">
              <mat-icon class="!h-auto !w-auto !text-2xl shrink-0 text-primary">
                work_outline
              </mat-icon>
              <div>
                <p class="font-bold text-gray-700">
                  {{ "CONTACT.JOB_SEEKER.TITLE" | translate }}
                </p>
                <p class="mt-1 text-sm text-gray-600">
                  {{ "CONTACT.JOB_SEEKER.BODY" | translate }}
                </p>
                <!-- Wrapped: `.button` is `display:flex`, so a flex parent is
                     what keeps it shrink-to-fit instead of full-bleed. -->
                <div class="mt-4 flex">
                  <a
                    [routerLink]="jobSeekerLink"
                    class="button primary justify-center !text-sm"
                  >
                    {{ "CONTACT.JOB_SEEKER.CTA" | translate }}
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="md:col-span-2">
            <label class="label" for="contact-message">
              {{ "CONTACT.FIELDS.MESSAGE" | translate }}
            </label>
            <textarea
              id="contact-message"
              class="input contact-message"
              rows="6"
              formControlName="message"
            ></textarea>
            <p class="validation mt-1" *ngIf="fieldErrors['message']">
              {{ fieldErrors["message"] }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('message', 'required')">
              {{ "CONTACT.VALIDATION.MESSAGE_REQUIRED" | translate }}
            </p>
            <p class="validation mt-1" *ngIf="hasError('message', 'maxlength')">
              {{ "CONTACT.VALIDATION.TOO_LONG" | translate }}
            </p>
          </div>
        </div>

        <div
          *ngIf="status === 'error' && errorMessage"
          class="mt-5 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {{ errorMessage | translate }}
        </div>

        <div class="mt-7 flex flex-col md:flex-row md:items-center gap-4">
          <button
            type="submit"
            class="contact-submit button primary justify-center gap-x-2 w-full md:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
            [disabled]="submitting"
          >
            <mat-spinner *ngIf="submitting" diameter="20"></mat-spinner>
            <span>
              {{
                (submitting ? "CONTACT.SENDING" : "CONTACT.SUBMIT") | translate
              }}
            </span>
          </button>
          <p class="text-sm text-gray-500">
            {{ "CONTACT.REPLY_NOTE" | translate }}
          </p>
        </div>
      </form>
    </ng-template>


    <!-- Call Us Section -->
    <div data-aos="fade" [attr.data-aos-delay]="600">
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>
        <h2 class="text-xl md:text-2xl font-semibold text-gray-700">Call Us</h2>
      </div>
      
      <a href="tel:+966506926575" 
         class="group block bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-6 md:p-8 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 md:w-8 md:h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 mb-1">Get in touch directly</p>
              <p class="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-primary transition-colors duration-300" dir="ltr">
                +966 50 692 6575
              </p>
            </div>
          </div>
          <div class="hidden md:flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span class="text-sm font-medium">Call Now</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
        <div class="mt-4 pt-4 border-t border-primary/10">
          <p class="text-sm text-gray-500 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Available during business hours (Sun - Thu, 10 AM - 5 PM KSA)
          </p>
        </div>
      </a>
    </div>
  </div>
</div>
